import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Fade,
  FormControl,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  SxProps,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { useConfirm } from 'material-ui-confirm';

import {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';
import OneToOneTutorRateVersionsModal from './OneToOneTutorRateVersionsModal';
import StudentTypesModal from './StudentTypesModal';
import { useAppSelector } from '../../../../../../hooks';
import { getAuthState } from '../../../../../../store/AuthSlice';
import { checkUserPermission } from '../../../../../../helper';
import AddNewOneToOneTutorRateModal from './AddNewOneToOneTutorRateModal';
import useTable, {
  defaultRowsPerPageOptions,
  TUseTableState,
} from '../../../../../../components/V2/Table/type1/useTable';
import TableHeadCustom, {
  THeadLabelItem,
} from '../../../../../../components/V2/Table/type1/TableHeadCustom';
import TableLoader from '../../../../../../components/Loaders/TableLoader';
import TableNoData from '../../../../../../components/V2/Empty/TableNoData';
import TablePaginationActions from '../../../../../../components/V2/Table/type1/TablePaginationActions';
import OneToOneTutorRateTableRow from './OneToOneTutorRateTableRow';
import {
  countryNameOptions,
  TCountryNameOption,
} from '../../../../../../constants';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../components/Inputs';
import OneToOneTutorRateStudentTypeListDropdown, {
  TOneToOneTutorRateStudentTypeOption,
} from '../../../../../../components/Inputs/admin/OneToOneTutorRateStudentTypeListDropdown';
import {
  adminDeleteOneToOneTutorRate,
  adminGetOneToOneTutorRates,
  getOneToOneTutorRatesState,
  IOneToOneTutorRate,
  resetAdminOneToOneTutorRateListData,
  TAdminGetOneToOneTutorRates,
} from '../../../../../../store/V2/OneToOneTutorRatesSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../store/V2/RolesSlice';

type TState = {
  selectedTutorCountry: TCountryNameOption | null;
  selectedStudentCountry: TCountryNameOption | null;
  selectedStudentType: TOneToOneTutorRateStudentTypeOption | null;
};

type TFilterData = {
  limit: number;
  offset: number;
  sortKey: string;
  sortValue: number;
  tutorCountry?: string;
  studentCountry?: string;
  studentType?: string;
};

const tableCardContainerStyles: SxProps = {
  borderRadius: '20px',
  boxShadow: '0px 14px 40px 0px #6E9DB22E',
};

const textFieldProps: TextFieldProps = {
  sx: {
    backgroundColor: '#ffffff',
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const defaultListLimit = 30;
const defaultOrderBy = 'createdAt';
const defaultOrder = 'desc';
const defaultTableData: TUseTableState = {
  defaultOrderBy,
  defaultOrder,
  defaultDense: false,
  defaultCurrentPage: 0,
  defaultRowsPerPage: defaultRowsPerPageOptions[0],
  defaultSelected: [],
};

const defaultState: TState = {
  selectedTutorCountry: null,
  selectedStudentCountry: null,
  selectedStudentType: null,
};

const defaultFilterData: TFilterData = {
  offset: 0,
  limit: defaultListLimit,
  sortKey: defaultOrderBy,
  sortValue: defaultOrder === 'desc' ? -1 : 1,
};

const internationalSelectOption = {
  label: 'International',
  value: 'International',
};

const studentCountryOptions = [
  internationalSelectOption,
  ...countryNameOptions,
];

const tableHeaderLabels: THeadLabelItem[] = [
  {
    id: 'tutorCountry',
    label: 'Tutor Country',
    sortable: true,
  },
  {
    id: 'studentCountry',
    label: 'Student Country',
    sortable: true,
  },
  {
    id: 'studentType',
    label: 'Student Type',
    sortable: false,
  },
  {
    id: 'actions',
    label: 'Actions',
    sortable: false,
    width: 200,
    minWidth: 200,
  },
];

const OneToOneTutorRatesTab: FunctionComponent = () => {
  const dispatch = useDispatch();
  const confirm = useConfirm();

  // Store
  const authState = useAppSelector(getAuthState);
  const oneToOneTutorRatesState = useAppSelector(getOneToOneTutorRatesState);

  // State
  const tableData = useTable(defaultTableData);
  const [state, setState] = useState({ ...defaultState });
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [filterData, setFilterData] = useState({ ...defaultFilterData });
  const [isRateVersionsModalOpen, setRateVersionsModalOpen] = useState(false);
  const [isStudentTypesModalOpen, setStudentTypesModalOpen] = useState(false);
  const [editModalData, setEditModalData] = useState<{
    open: boolean;
    oneToOneTutorRate?: IOneToOneTutorRate;
  }>({ open: false });

  const [viewModalData, setViewModalData] = useState<{
    open: boolean;
    oneToOneTutorRate?: IOneToOneTutorRate;
  }>({ open: false });

  const [duplicateModalData, setDuplicateModalData] = useState<{
    open: boolean;
    oneToOneTutorRate?: IOneToOneTutorRate;
  }>({ open: false });

  const authUser = authState.user;
  const createPermission = checkUserPermission(authUser, {
    adminSettings: [
      USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_RATES_CREATE,
    ],
  });

  let oneToOneTutorRates: IOneToOneTutorRate[] = [];
  let totalCount = 0;

  if (oneToOneTutorRatesState.adminOneToOneTutorRateListData) {
    totalCount =
      oneToOneTutorRatesState.adminOneToOneTutorRateListData.totalDocs || 0;

    if (
      oneToOneTutorRatesState.adminOneToOneTutorRateListData.list &&
      oneToOneTutorRatesState.adminOneToOneTutorRateListData.list.length
    ) {
      oneToOneTutorRates =
        oneToOneTutorRatesState.adminOneToOneTutorRateListData.list || [];
    }
  }

  useEffect(() => {
    getOneToOneTutorRates({ ...defaultFilterData });

    return () => {
      dispatch(resetAdminOneToOneTutorRateListData());
    };
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const getOneToOneTutorRates = async (filterDataAttributes: TFilterData) => {
    const apiData: TAdminGetOneToOneTutorRates = {
      limit: filterDataAttributes.limit,
      offset: filterDataAttributes.offset,
    };

    if (filterDataAttributes.sortKey) {
      apiData.sortKey = filterDataAttributes.sortKey;
    }

    if (filterDataAttributes.sortValue) {
      apiData.sortValue = filterDataAttributes.sortValue;
    }

    if (filterDataAttributes.tutorCountry) {
      apiData.tutorCountry = filterDataAttributes.tutorCountry;
    }

    if (filterDataAttributes.studentCountry) {
      apiData.studentCountry = filterDataAttributes.studentCountry;
    }

    if (filterDataAttributes.studentType) {
      apiData.studentType = filterDataAttributes.studentType;
    }

    dispatch(adminGetOneToOneTutorRates({ ...apiData }));

    setFilterData({ ...filterDataAttributes });
  };

  const handleSort = (id: string) => {
    let newSortValue = 1;

    if (filterData.sortKey === id && filterData.sortValue === 1) {
      newSortValue = -1;
    }

    getOneToOneTutorRates({
      ...filterData,
      sortKey: id,
      sortValue: newSortValue,
    });

    tableData.onSort(id);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    getOneToOneTutorRates({
      ...filterData,
      offset: page * filterData.limit,
    });

    tableData.onChangePage(event, page);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    getOneToOneTutorRates({
      ...filterData,
      offset: 0,
      limit: parseInt(event.target.value, 10),
    });

    tableData.onChangeRowsPerPage(event);
  };

  const handleDelete = (tutorRate: IOneToOneTutorRate) => {
    confirm({
      title: 'Are you sure?',
      description: 'Do you want to delete this tutor rate?',
    }).then(() => {
      dispatch(adminDeleteOneToOneTutorRate(tutorRate._id));
    });
  };

  return (
    <Fade in>
      <div>
        <Card sx={tableCardContainerStyles}>
          <CardContent style={{ padding: '20px 0' }}>
            <div style={{ padding: '20px' }}>
              <div>
                <Typography fontSize="24px" fontWeight={600}>
                  One to One Tutor Rates
                </Typography>
              </div>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                gap="10px"
              >
                <DarkButton
                  variant="contained"
                  color="inherit"
                  sx={{ ...defaultButtonStyles }}
                  onClick={() => setRateVersionsModalOpen(true)}
                >
                  Rate Versions
                </DarkButton>
                <DarkButton
                  variant="contained"
                  color="inherit"
                  sx={{ ...defaultButtonStyles }}
                  onClick={() => setStudentTypesModalOpen(true)}
                >
                  Student Types
                </DarkButton>
                {createPermission ? (
                  <DarkButton
                    variant="contained"
                    color="inherit"
                    sx={{ ...defaultButtonStyles }}
                    onClick={() => setAddModalOpen(true)}
                  >
                    Add New Tutor Rate
                  </DarkButton>
                ) : null}
              </Stack>
            </div>
            <div style={{ marginBottom: '20px', padding: '0 20px' }}>
              <Grid container columnSpacing="16px" rowSpacing="20px">
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControl fullWidth>
                    <Autocomplete
                      fullWidth
                      size="small"
                      value={state.selectedTutorCountry}
                      options={countryNameOptions}
                      popupIcon={TPAutoCompleteArrowIconComponent}
                      onChange={(e, value) => {
                        updateStateAttributes({ selectedTutorCountry: value });

                        getOneToOneTutorRates({
                          ...filterData,
                          tutorCountry: value ? value.value : undefined,
                        });
                      }}
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...textFieldProps}
                          size="small"
                          label="Tutor Country *"
                          placeholder="Select"
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={3}>
                  <FormControl fullWidth>
                    <Autocomplete
                      fullWidth
                      size="small"
                      value={state.selectedStudentCountry}
                      options={studentCountryOptions}
                      popupIcon={TPAutoCompleteArrowIconComponent}
                      onChange={(e, value) => {
                        updateStateAttributes({
                          selectedStudentCountry: value,
                        });

                        getOneToOneTutorRates({
                          ...filterData,
                          studentCountry: value ? value.value : undefined,
                        });
                      }}
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...textFieldProps}
                          size="small"
                          label="Student Country *"
                          placeholder="Select"
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControl fullWidth>
                    <OneToOneTutorRateStudentTypeListDropdown
                      selectedItem={state.selectedStudentType}
                      componentProps={{
                        fullWidth: true,
                      }}
                      handleChange={(e, value) => {
                        updateStateAttributes({
                          selectedStudentType: value,
                        });

                        getOneToOneTutorRates({
                          ...filterData,
                          studentType: value ? value.value : undefined,
                        });
                      }}
                      textFieldProps={{
                        label: 'Student Type *',
                        placeholder: 'Select',
                        ...textFieldProps,
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </div>
            <TableContainer
              sx={{ position: 'relative', maxHeight: 'calc(100vh - 250px)' }}
            >
              <Table stickyHeader size={tableData.dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={tableData.order}
                  orderBy={tableData.orderBy}
                  headLabels={tableHeaderLabels}
                  rowCount={0}
                  numSelected={tableData.selected.length}
                  onSort={handleSort}
                />
                <TableBody>
                  {oneToOneTutorRates.map((row) => (
                    <OneToOneTutorRateTableRow
                      key={row._id}
                      oneToOneTutorRate={row}
                      onDelete={() => handleDelete(row)}
                      onUpdate={() =>
                        setEditModalData({
                          open: true,
                          oneToOneTutorRate: row,
                        })
                      }
                      onView={() =>
                        setViewModalData({
                          open: true,
                          oneToOneTutorRate: row,
                        })
                      }
                      onDuplicate={() =>
                        setDuplicateModalData({
                          open: true,
                          oneToOneTutorRate: row,
                        })
                      }
                    />
                  ))}

                  {oneToOneTutorRatesState.status !== 'loading' &&
                  !oneToOneTutorRates.length ? (
                    <TableNoData title="No one to one tutor rates are available" />
                  ) : null}

                  {oneToOneTutorRatesState.status === 'loading' &&
                  !oneToOneTutorRates.length ? (
                    <TableLoader />
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={defaultRowsPerPageOptions}
                component="div"
                count={totalCount}
                rowsPerPage={tableData.rowsPerPage}
                page={tableData.page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleRowsPerPageChange}
                ActionsComponent={TablePaginationActions}
              />

              <FormControlLabel
                label="Dense"
                sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
                control={
                  <Switch
                    checked={tableData.dense}
                    onChange={tableData.onChangeDense}
                  />
                }
              />
            </Box>
          </CardContent>
        </Card>
        {isRateVersionsModalOpen ? (
          <OneToOneTutorRateVersionsModal
            open={isRateVersionsModalOpen}
            onClose={() => {
              setRateVersionsModalOpen(false);
              getOneToOneTutorRates({
                ...filterData,
              });
            }}
          />
        ) : null}
        {isStudentTypesModalOpen ? (
          <StudentTypesModal
            open={isStudentTypesModalOpen}
            onClose={() => {
              setStudentTypesModalOpen(false);
              getOneToOneTutorRates({
                ...filterData,
              });
            }}
          />
        ) : null}
        {isAddModalOpen ? (
          <AddNewOneToOneTutorRateModal
            open={isAddModalOpen}
            onClose={() => setAddModalOpen(false)}
          />
        ) : null}
        {editModalData.open && editModalData.oneToOneTutorRate ? (
          <AddNewOneToOneTutorRateModal
            open={editModalData.open}
            onClose={() => setEditModalData({ open: false })}
            oneToOneTutorRate={editModalData.oneToOneTutorRate}
          />
        ) : null}
        {viewModalData.open && viewModalData.oneToOneTutorRate ? (
          <AddNewOneToOneTutorRateModal
            readOnly
            open={viewModalData.open}
            onClose={() => setViewModalData({ open: false })}
            oneToOneTutorRate={viewModalData.oneToOneTutorRate}
          />
        ) : null}
        {duplicateModalData.open && duplicateModalData.oneToOneTutorRate ? (
          <AddNewOneToOneTutorRateModal
            duplicate
            open={duplicateModalData.open}
            onClose={() => setDuplicateModalData({ open: false })}
            oneToOneTutorRate={duplicateModalData.oneToOneTutorRate}
          />
        ) : null}
      </div>
    </Fade>
  );
};

export default OneToOneTutorRatesTab;
